import { defineStore } from 'pinia';

export interface AppNotification {
    id: string;
    title?: string;
    message: string;
    type: string; // 'ticket_message', 'FORM_SUBMISSION_RECEIVED', 'system', etc.
    read: boolean;
    created_at: string;
    metadata?: Record<string, unknown>;
    /**
     * False for a live item the backend did not persist (group-targeted
     * fan-out still broadcasts with `id: null`, so the client invents one).
     * Those cannot be marked read server-side — see useNotificationCentre.
     */
    persisted?: boolean;
}

/**
 * The bell feed (#224). Notifications are persisted server-side, so this store
 * is a view of server state rather than the source of truth: `useNotificationCentre`
 * hydrates it from `myNotifications`, keeps `serverUnreadCount` in step with
 * `myUnreadNotificationCount`, and calls the mark-read mutations. The mutating
 * actions here only move local state — call them through the composable so the
 * server is written too.
 */
export const useNotificationStore = defineStore('notification', () => {
  const notifications = ref<AppNotification[]>([]);
  const unreadTicketCount = ref(0);
  /**
   * Unread total as the server counts it. `notifications` only ever holds the
   * pages we fetched, so counting unread rows locally would cap the badge at
   * the page size; -1 means "not hydrated yet, fall back to the local count".
   */
  const serverUnreadCount = ref(-1);

  // Getters
  const localUnreadCount = computed(() => notifications.value.filter(n => !n.read).length);
  const unreadCount = computed(() => (
    serverUnreadCount.value >= 0 ? serverUnreadCount.value : localUnreadCount.value
  ));
  const sortedNotifications = computed(() => [...notifications.value].sort((a, b) =>
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  ));

  // Actions
  function addNotification(notification: AppNotification) {
    // Dedupe by server id: a persisted notification can reach us twice — once
    // live over the subscription and again on the next hydration.
    if (notifications.value.some(n => n.id === notification.id)) return;

    notifications.value.unshift(notification);

    if (notification.type === 'ticket_message') {
      unreadTicketCount.value++;
    }
    if (!notification.read && serverUnreadCount.value >= 0) {
      serverUnreadCount.value++;
    }
  }

  /**
   * Replace the feed with a server page, preserving any live-only items that
   * the server never persisted (they would otherwise vanish on hydration).
   */
  function hydrate(serverNotifications: AppNotification[]) {
    const serverIds = new Set(serverNotifications.map(n => n.id));
    const liveOnly = notifications.value.filter(n => n.persisted === false && !serverIds.has(n.id));
    notifications.value = [...serverNotifications, ...liveOnly];
  }

  function setServerUnreadCount(count: number) {
    serverUnreadCount.value = Math.max(0, count);
  }

  function markAllAsRead() {
    notifications.value.forEach(n => n.read = true);
    unreadTicketCount.value = 0; // Or recalculate if we keep ticket counts separate
    if (serverUnreadCount.value >= 0) serverUnreadCount.value = 0;
  }

  function markAsRead(id: string) {
    const notif = notifications.value.find(n => n.id === id);
    if (notif && !notif.read) {
      notif.read = true;
      if (notif.type === 'ticket_message') {
        unreadTicketCount.value = Math.max(0, unreadTicketCount.value - 1);
      }
      if (serverUnreadCount.value > 0) {
        serverUnreadCount.value--;
      }
    }
  }

  /** Undo an optimistic markAsRead when the mutation fails. */
  function markAsUnread(id: string) {
    const notif = notifications.value.find(n => n.id === id);
    if (notif && notif.read) {
      notif.read = false;
      if (notif.type === 'ticket_message') {
        unreadTicketCount.value++;
      }
      if (serverUnreadCount.value >= 0) {
        serverUnreadCount.value++;
      }
    }
  }

  function clearTicketBadge() {
    unreadTicketCount.value = 0;
  }

  // Widget Trigger
  const widgetTrigger = ref<number>(0);
  function triggerOpenWidget() {
    widgetTrigger.value++;
  }

  return {
    notifications,
    unreadTicketCount,
    serverUnreadCount,
    unreadCount,
    sortedNotifications,
    widgetTrigger, // Export
    addNotification,
    hydrate,
    setServerUnreadCount,
    markAllAsRead,
    markAsRead,
    markAsUnread,
    clearTicketBadge,
    triggerOpenWidget, // Export
  };
});
